import styled from '@emotion/styled'
import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useAntMedia } from 'providers/AntMediaProvider'
import { useEffect, useRef } from 'react'
import { useEvent } from 'react-use'
import { mediaQueries } from '../../core/theme'
import { useStore } from '../../stores'
import { StreamData } from '../../stores/Roulette/StreamData.store'

type VideoProps = {
  data: StreamData
  offsetHeight: (data: number) => void
}

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-position: bottom;

  ${mediaQueries.medium} {
    object-fit: contain;
    height: auto;
  }
`

const VIDEO_ID = 'videoRoulette'

const Video = observer((props: VideoProps): JSX.Element => {
  const { data, offsetHeight } = props
  const { rouletteStore } = useStore()
  const { volumeStore } = rouletteStore
  const { play, stop, connectionState } = useAntMedia()
  const ref = useRef<HTMLVideoElement | null>(null)
  const getOffsetHeight = (): void => {
    if (!ref.current) return

    offsetHeight(ref.current.offsetHeight)
  }

  const { streamId } = data

  useEffect(() => {
    return () => {
      stop()
    }
  }, [])

  useEffect(() => {
    if (connectionState === 'ready' && streamId && ref.current) {
      play(streamId, ref.current)
    }
  }, [connectionState, streamId, play])

  useEffect(() => {
    if (ref.current) {
      ref.current.volume = volumeStore.volume
    }
  }, [ref, volumeStore.volume])

  useEvent('resize', debounce(getOffsetHeight, 300))
  useEvent('orientationchange', debounce(getOffsetHeight, 100))

  return (
    <StyledVideo
      onPlay={() => {
        if (ref.current) offsetHeight(ref.current.offsetHeight)
      }}
      ref={ref}
      id={VIDEO_ID}
      autoPlay
      playsInline
      muted={volumeStore.isMuted}
    />
  )
})

export default Video
